/**
 * Onboarding Modal
 * 
 * Shows welcome message and tutorial information to new players
 * Displayed once per account, tracked in Firestore
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { PLAYER_MARKERS, DEFAULT_MARKER } from '../utils/playerMarkers';
import './OnboardingModal.css';

interface OnboardingModalProps {
  onClose: () => void;
  onMarkerSelect: (markerId: string) => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ onClose, onMarkerSelect }) => {
  const [selectedMarker, setSelectedMarker] = useState<string>(DEFAULT_MARKER);

  const handleBegin = () => {
    onMarkerSelect(selectedMarker);
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="onboarding-modal-overlay" onClick={onClose}>
      <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
        <div className="onboarding-content">
          <h1 className="onboarding-title">Welcome to Folklore World, Adventurer</h1>
          
          <p className="onboarding-intro">
            You stand at the edge of a vast, living world—one that <strong>you will help write</strong>.
          </p>
          
          <h2 className="onboarding-heading">The Basics</h2>
          
          <p className="onboarding-text">
            <strong>Explore the Map</strong>: Use the <strong>mini map</strong> to tap adjacent hexes 
            for quick steps, or click "View Map" to open the <strong>full map</strong> where you can 
            plan longer journeys to distant hexes. Each step costs stamina, which resets daily. 
            Longer journeys risk monster encounters along the way.
          </p>
          
          <p className="onboarding-text">
            <strong>Face Dangers</strong>: Monsters lurk in the wilderness. Defeat them to continue 
            your journey, or fall and respawn at your home settlement.
          </p>
          
          <p className="onboarding-text">
            <strong>Experience Stories</strong>: Every hex has a tale. Some you'll encounter, others 
            you'll create.
          </p>
          
          <h2 className="onboarding-heading">Your Unique Power: First Discovery</h2>
          
          <p className="onboarding-text">
            When you see <strong>"You are in unexplored land"</strong>, you've found something 
            special—a hex no one has ever visited.
          </p>
          
          <p className="onboarding-text">
            <strong>On unexplored hexes, the Quest button generates a completely new adventure</strong>—one 
            that has never existed before. You're not just playing content; you're <strong>creating it</strong>.
          </p>
          
          <p className="onboarding-text">
            <strong>Complete that quest, and it becomes permanent</strong>. Future players who visit 
            this hex will encounter the same adventure, making their own choices and forging their 
            own path through the story YOU discovered.
          </p>
          
          <p className="onboarding-text">
            <strong>During quests, you can flee if overwhelmed</strong>—failure means the quest 
            remains for another brave soul to attempt.
          </p>
          
          <h2 className="onboarding-heading">Shaping the World</h2>
          
          <ul className="onboarding-list">
            <li><strong>Unexplored hexes</strong> → Generate new quests → Complete them → They bind to that location forever</li>
            <li><strong>Explored hexes</strong> → Play existing quests that other players have completed, or find uncompleted quests waiting for their first victor</li>
            <li><strong>Every discovery matters</strong> → Your name, your decisions, your triumphs become part of the living tapestry</li>
          </ul>
          
          <p className="onboarding-outro">
            You're not just an adventurer passing through. You're a <strong>chronicler, a pioneer, a legend-maker</strong>.
          </p>
          
          <p className="onboarding-outro">
            The map awaits. Will you leave your mark?
          </p>
          
          {/* Character Selection */}
          <div className="marker-selection-section">
            <h2 className="onboarding-heading">Choose Your Adventurer</h2>
            <div className="marker-grid">
              {PLAYER_MARKERS.map((marker) => (
                <button
                  key={marker.id}
                  className={`marker-option ${selectedMarker === marker.id ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMarker(marker.id);
                  }}
                  title={marker.name}
                >
                  <div className="marker-hexagon">
                    <img src={marker.image} alt={marker.name} />
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          <button onClick={handleBegin} className="onboarding-close-btn">
            Begin Your Journey
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

